import { useNavigate } from 'react-router-dom';
import { Heart, AlertCircle, CheckCircle, XCircle, Clock } from 'lucide-react';

export default function RefundPolicy() {
  const navigate = useNavigate();

  return (
    <div className="app-container">
      <div className="px-4 py-6 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/')} className="text-white">
            ← Back
          </button>
          <Heart className="text-primary fill-primary" size={24} />
          <div className="w-8" />
        </div>

        <h1 className="text-white font-bold text-3xl mb-6">Cancellation & Refund Policy</h1>

        <div className="space-y-6 text-gray-300 text-sm leading-relaxed">
          {/* Overview */}
          <div className="glass rounded-xl p-4 border border-primary/30">
            <p className="text-white">
              BlindMatch is a digital matchmaking service. Match availability depends on various factors including location, interests, and user availability. Our refund policy is designed to be fair while protecting the integrity of our service.
            </p>
          </div>

          <section>
            <h2 className="text-white font-semibold text-lg mb-3 flex items-center gap-2">
              <CheckCircle className="text-green-400" size={20} />
              Eligible for Refund
            </h2>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span><strong>Payment failed but amount deducted:</strong> Full refund within 5-7 business days</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span><strong>Double payment charged:</strong> Duplicate amount refunded within 5-7 business days</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span><strong>Service unavailable:</strong> If we cannot provide the service due to technical issues on our end, full refund within 7 business days</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span><strong>Account banned unfairly:</strong> If your account is banned incorrectly and you have unused match credits, eligible for partial refund</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-white font-semibold text-lg mb-3 flex items-center gap-2">
              <XCircle className="text-red-400" size={20} />
              Not Eligible for Refund
            </h2>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span><strong>Matches already delivered:</strong> Once matches are generated and shown to you, refunds cannot be processed</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span><strong>No compatible matches found:</strong> If there are no compatible users in your area, we add you to a waitlist but cannot refund</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span><strong>Dissatisfaction with matches:</strong> As matching is based on preferences you set, we cannot refund due to personal dissatisfaction</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span><strong>Account banned for violations:</strong> Users banned for harassment, spam, or policy violations are not eligible for refunds</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span><strong>Change of mind:</strong> Refunds are not available simply because you changed your mind after purchase</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-white font-semibold text-lg mb-3 flex items-center gap-2">
              <Clock className="text-amber-400" size={20} />
              Refund Timeline
            </h2>
            <div className="glass rounded-xl p-4 border border-white/10">
              <ul className="space-y-2">
                <li><strong>Request Processing:</strong> 2-3 business days</li>
                <li><strong>Bank/UPI Refund:</strong> 5-7 business days after approval</li>
                <li><strong>Credit/Debit Card Refund:</strong> 7-10 business days after approval</li>
                <li><strong>Net Banking Refund:</strong> 5-7 business days after approval</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-white font-semibold text-lg mb-3">How to Request a Refund</h2>
            <ol className="space-y-3 list-decimal list-inside">
              <li>Email us at <a href="mailto:payments@blindmatch.in" className="text-primary hover:underline">payments@blindmatch.in</a></li>
              <li>Include your registered email address</li>
              <li>Provide the transaction ID or payment reference</li>
              <li>Describe the reason for your refund request</li>
              <li>Attach any relevant screenshots (payment failure, etc.)</li>
            </ol>
          </section>

          <section>
            <h2 className="text-white font-semibold text-lg mb-3">Cancellation Policy</h2>
            <div className="glass rounded-xl p-4 border border-white/10">
              <p className="mb-2">
                <strong>Before Payment:</strong> You can cancel at any time before making a payment. Simply close the payment window.
              </p>
              <p className="mb-2">
                <strong>During Payment:</strong> If you cancel during payment processing, any deducted amount will be automatically refunded by your bank within 5-7 days.
              </p>
              <p>
                <strong>After Payment:</strong> Once payment is successful and match credits are added to your account, the service is considered active and cannot be cancelled.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-white font-semibold text-lg mb-3">Contact for Refunds</h2>
            <div className="glass rounded-xl p-4 border border-primary/30">
              <p className="mb-2">
                <strong>Email:</strong> <a href="mailto:payments@blindmatch.in" className="text-primary hover:underline">payments@blindmatch.in</a>
              </p>
              <p className="text-gray-400 text-xs">
                Support available 24/7
              </p>
            </div>
          </section>

          <section className="text-xs text-gray-500">
            <p>Last updated: March 2026</p>
            <p>This policy is subject to change. Check back periodically for updates.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
